import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import _ from "lodash";

const COLORS = d3.schemeCategory10; // 10 distinct colors

const BreakdownScoreMultiLineChart = ({ data }) => {
    const svgRef = useRef();

    useEffect(() => {
        if (!data || data.length === 0) return;

        const margin = { top: 50, right: 100, bottom: 80, left: 50 };
        const containerWidth = svgRef.current.parentElement.offsetWidth;
        const width = containerWidth - margin.left - margin.right - 200; // reserve space for legend
        const height = 400 - margin.top - margin.bottom;

        const svg = d3.select(svgRef.current);
        svg.selectAll('*').remove(); // Clear previous content

        const g = svg
            .attr('width', width + margin.left + margin.right)
            .attr('height', height + margin.top + margin.bottom)
            .append('g')
            .attr('transform', `translate(${margin.left},${margin.top})`);

        // Flatten data to extract all question labels
        const questionSet = new Set();
        data.forEach(d => {
            d.breakDownScore.breakdown.forEach(b => {
                questionSet.add(b.question);
            });
        });
        const questions = Array.from(questionSet);

         const getDateInRange = (startDate, endDate)  =>{
            const start = startDate.getTime();
            const end = endDate.getTime();
            const randomTime = start + Math.random() * (end - start);
            return new Date(randomTime);
        }

        // Processed data: { question1: [{date, score}], question2: [...] }
        const series = questions.map((question) => ({
            question,
            values: data
                .map(d => {
                    const b = d.breakDownScore.breakdown.find(x => x.question === question);
                    //TODO
                    return b ? { date: new Date(d.date), score: b.breakdownScore } : null;
                    // return b ? { date: new Date(d.date), score: _.random(3,5,true) } : null;
                })
                .filter(Boolean)
        }));

        // Scales
        const xExtent = d3.extent(data, d => new Date(d.date));
        const xPadding = 1000 * 60 * 60 * 12; // 12 hours
        const xScale = d3.scaleTime()
            .domain([new Date(xExtent[0].getTime() - xPadding), new Date(xExtent[1].getTime() + xPadding)])
            .range([0, width]);

        const yScale = d3.scaleLinear()
            .domain([0, 5])
            .range([height, 0]);


        // X-axis with rotated labels
        const xAxis = g.append('g')
            .attr('transform', `translate(0,${height})`)
            .call(
                d3.axisBottom(xScale)
                    .ticks(data.length)
                    .tickFormat(d3.timeFormat('%Y-%m-%d'))
            );

        //Y-Axis with score labels
        const yAxis = d3.axisLeft(yScale)
            .ticks(5)             // Ensures there are 5 ticks (optional)
            .tickFormat(d3.format("d")); // Formats ticks as integers

        xAxis.selectAll('text')
            .attr('transform', 'rotate(-90)')
            .style('text-anchor', 'end')
            .attr('dx', '-0.5em')
            .attr('dy', '0.25em')
            .style('font-size', '14px');


        // Axes
        g.append('g')
            .call(yAxis);

        // Line generator
        const line = d3.line()
            .x(d => xScale(d.date))
            .y(d => yScale(d.score))
            .curve(d3.curveMonotoneX);

        // Draw lines
        series.forEach((s, i) => {
            g.append('path')
                .datum(s.values)
                .attr('fill', 'none')
                .attr('stroke', COLORS[i % COLORS.length])
                .attr('stroke-width', 2)
                .attr('d', line);

            // Add label to the end of line
            const lastPoint = s.values[s.values.length - 1];
            if (lastPoint && data.length > 10) {
                g.append('text')
                    .attr('x', xScale(lastPoint.date) + 5)
                    .attr('y', yScale(lastPoint.score))
                    .attr('fill', COLORS[i % COLORS.length])
                    // .text(s.question)
                    .style('font-size', '12px');
            }

            // Tooltip circles
            s.values.forEach(point => {
                svg.append('circle')
                    .attr('cx', xScale(point.date) + margin.left)
                    .attr('cy', yScale(point.score) + margin.top)
                    .attr('r', 4)
                    .attr('fill', COLORS[i % COLORS.length])
                    .each(function () {
                        d3.select(this)
                            .append('title')
                            .text(`${s.question}\n${d3.timeFormat('%Y-%m-%d')(point.date)}: ${point.score.toFixed(2)}`);
                    });
            });
        });

        // Legend
        const legendX = width + margin.left + 60;
        const legendY = (height - series.length * 20) / 2 + margin.top;
        const legend = svg.append('g')
            .attr('transform', `translate(${legendX}, ${legendY})`);

        series.forEach((s, i) => {
            const yOffset = i * 20;
            legend.append('rect')
                .attr('x', 0)
                .attr('y', yOffset)
                .attr('width', 12)
                .attr('height', 12)
                .attr('fill', COLORS[i % COLORS.length]);

            const maxLabelLength = 30;
            const labelText = s.question.length > maxLabelLength
                ? s.question.slice(0, maxLabelLength) + '…'
                : s.question;

            const text = legend.append('text')
                .attr('x', 20)
                .attr('y', yOffset + 10)
                .text(labelText)
                .style('font-size', '12px')
                .attr('alignment-baseline', 'middle');

            text.append('title').text(s.question); // Tooltip with full text
        });

    }, [data]);

    return (
        <div style={{ overflow: 'visible'}}>
            <svg ref={svgRef} style={{width: '100%', height: '400px', overflow: 'visible'}}/>
        </div>
    );
};

export default BreakdownScoreMultiLineChart;
